import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import API_CLIENT from "../../lib/api-client";
import { LOGIN_ROUTE, SIGNUP_ROUTE, SEND_OTP_ROUTE, VERIFY_OTP_ROUTE } from "../../utils/constant";
import { useAuthStore } from "../../store/slice/authSlice";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser, faEnvelope, faLock, faPhone, faBriefcase,
  faIdCard, faArrowRight, faArrowLeft, faCheckCircle,
  faShieldAlt, faEye, faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import LOGO from "../../assets/Logo.png";

/* ════════════════════════════════════════════════════════════════
   COMPONENTS
════════════════════════════════════════════════════════════════ */

const STEPS = [
  { label: "Basic Info",    icon: faUser,         key: 1 },
  { label: "Role Details",  icon: faBriefcase,    key: 2 },
  // { label: "Verify Email",  icon: faShieldAlt,    key: 3 },
  { label: "Confirm",       icon: faCheckCircle,  key: 3 },
];

const Field = ({ label, icon, type = "text", id, value, onChange, placeholder, required, children, hint }) => (
  <div style={{ marginBottom: 16 }}>
    <label htmlFor={id} style={S.label}>
      {label}{required && <span style={{ color: "#0d9488" }}>*</span>}
    </label>
    <div style={{ position: "relative" }}>
      <FontAwesomeIcon icon={icon} style={S.fieldIcon} />
      {children || (
        <input id={id} type={type} value={value} onChange={onChange}
          placeholder={placeholder} required={required}
          style={S.input}
          onFocus={e => (e.target.style.borderColor = "#0d9488")}
          onBlur={e => (e.target.style.borderColor = "#e5e7eb")}
        />
      )}
    </div>
    {hint && <p style={S.hint}>{hint}</p>}
  </div>
);

const SelectField = ({ label, icon, id, value, onChange, required, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label htmlFor={id} style={S.label}>
      {label}{required && <span style={{ color: "#0d9488" }}>*</span>}
    </label>
    <div style={{ position: "relative" }}>
      <FontAwesomeIcon icon={icon} style={S.fieldIcon} />
      <select id={id} value={value} onChange={onChange} required={required}
        style={{ ...S.input, cursor: "pointer" }}
        onFocus={e => (e.target.style.borderColor = "#0d9488")}
        onBlur={e => (e.target.style.borderColor = "#e5e7eb")}
      >
        {children}
      </select>
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════════════════════════ */
const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setToken, setUserInfo } = useAuthStore();
  
  // Decide which tab is active based on url route, default to login
  const [activeTab, setActiveTab] = useState(location.pathname.includes("register") ? "register" : "login");
  
  // Update state when URL changes directly
  useEffect(() => {
    setActiveTab(location.pathname.includes("register") ? "register" : "login");
  }, [location.pathname]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    // update URL without full reload (so animations frame smoothly)
    window.history.pushState(null, '', `/${tab}`);
  };

  /* ── login state ── */
  const [loginData, setLoginData] = useState({ emailId: "", password: "" });
  const [showLoginPass, setShowLoginPass] = useState(false);
  const [isLoginLoading, setIsLoginLoading] = useState(false);

  /* ── register state ── */
  const [step, setStep] = useState(1);
  const [isRegLoading, setIsRegLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [showRegPass, setShowRegPass] = useState(false);
  const [regData, setRegData] = useState({
    userName: "", emailId: "", password: "", phoneNo: "",
    role: "", companyName: "", businessLicense: "", gstNumber: "",
  });

  /* ─────────────────────────────────────────────────────────
     LOGIN LOGIC
  ───────────────────────────────────────────────────────── */
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setIsLoginLoading(true);
    try {
      const res = await API_CLIENT.post(LOGIN_ROUTE, loginData);
      if (res.status === 200 && res.data.success) {
        setToken(res.data.token);
        setUserInfo(res.data.user);
        toast.success(res.data.message || "Login successful");
        navigate("/");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Login failed. Please check your credentials.");
    }
    setIsLoginLoading(false);
  };

  /* ─────────────────────────────────────────────────────────
     REGISTER LOGIC
  ───────────────────────────────────────────────────────── */
  const setR = (k) => (e) => setRegData(p => ({ ...p, [k]: e.target.value }));

  // auto send otp on step 3 (disabled)
  /*
  useEffect(() => {
    if (activeTab === "register" && step === 3 && !otpSent) sendOtp();
  }, [activeTab, step]);

  const sendOtp = async () => {
    try {
      setIsRegLoading(true);
      const res = await API_CLIENT.post(SEND_OTP_ROUTE, { emailId: regData.emailId });
      if (res.status === 200 && res.data.success) {
        toast.success(res.data.message || "OTP sent!");
        setOtpSent(true);
      } else toast.error(res.data.message || "Failed to send OTP");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send OTP");
    } finally { setIsRegLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 4) { toast.error("Please enter a valid OTP"); return; }
    try {
      setIsRegLoading(true);
      const res = await API_CLIENT.post(VERIFY_OTP_ROUTE, { emailId: regData.emailId, otp });
      if (res.status === 200 && res.data.success) { toast.success("Email verified!"); setStep(4); }
      else toast.error(res.data.message || "Invalid OTP");
    } catch (err) {
      toast.error(err.response?.data?.message || "OTP verification failed");
    } finally { setIsRegLoading(false); }
  };
  */

  const nextStep = () => {
    if (step === 1 && (!regData.userName || !regData.emailId || !regData.password)) {
      toast.error("Please fill all required fields"); return;
    }
    if (step === 2 && !regData.role) { toast.error("Please select a role"); return; }
    if (step === 2 && regData.role === "manufacturer" && (!regData.companyName || !regData.businessLicense)) {
      toast.error("Please fill company details"); return;
    }
    setStep(s => s + 1);
  };

  const handleRegSubmit = async (e) => {
    e.preventDefault();
    // if (step === 3) { verifyOtp(e); return; }
    if (step === 3) {
      setIsRegLoading(true);
      try {
        const res = await API_CLIENT.post(SIGNUP_ROUTE, regData);
        if (res.status === 201 && res.data.success) {
          setToken(res.data.token);
          setUserInfo(res.data.user);
          toast.success(res.data.message || "Registration successful!");
          navigate("/");
        }
      } catch (err) {
        toast.error(err.response?.data?.message)
      }
      setIsRegLoading(false);
    } else { nextStep(); }
  };

  /* ── Register Step Render ── */
  const renderRegStep = () => {
    switch (step) {
      case 1: return (
        <div>
          <Field label="Username" icon={faUser} id="userName" value={regData.userName}
            onChange={setR("userName")} placeholder="johndoe" required />
          <Field label="Email Address" icon={faEnvelope} id="emailId" type="email"
            value={regData.emailId} onChange={setR("emailId")} placeholder="you@example.com" required />
          <Field label="Password" icon={faLock} id="regpassword">
            <input id="regpassword" type={showRegPass ? "text" : "password"}
              value={regData.password} onChange={setR("password")} placeholder="Create a strong password" required
              style={S.input}
              onFocus={e => (e.target.style.borderColor = "#0d9488")}
              onBlur={e => (e.target.style.borderColor = "#e5e7eb")}
            />
            <button type="button" onClick={() => setShowRegPass(!showRegPass)} style={S.eyeBtn}>
              <FontAwesomeIcon icon={showRegPass ? faEyeSlash : faEye} style={{ fontSize: ".8rem", color: "#9ca3af" }} />
            </button>
          </Field>
          <Field label="Phone Number" icon={faPhone} id="phoneNo" type="tel"
            value={regData.phoneNo} onChange={setR("phoneNo")} placeholder="+91 98765 43210" required hint="10-digit mobile number" />
        </div>
      );
      case 2: return (
        <div>
          <SelectField label="Your Role" icon={faBriefcase} id="role"
            value={regData.role} onChange={setR("role")} required>
            <option value="">Select your role…</option>
            <option value="manufacturer">Manufacturer</option>
            <option value="retailer">Retailer</option>
          </SelectField>
          {regData.role === "manufacturer" && (
            <>
              <Field label="Company Name" icon={faBriefcase} id="companyName"
                value={regData.companyName} onChange={setR("companyName")} placeholder="Your company name" required />
              <Field label="Business License" icon={faIdCard} id="businessLicense"
                value={regData.businessLicense} onChange={setR("businessLicense")} placeholder="License number" required />
            </>
          )}
          {regData.role === "retailer" && (
            <Field label="GST Number" icon={faIdCard} id="gstNumber"
              value={regData.gstNumber} onChange={setR("gstNumber")} placeholder="GSTIN number" />
          )}
          {!regData.role && (
            <div style={S.roleHint}>
              <p style={{ fontWeight: 600, color: "#0f766e", marginBottom: 4 }}>Choose your role:</p>
              <p style={{ fontSize: ".82rem", color: "#6b7280" }}><strong>Manufacturer</strong> — List your fabric products and reach thousands of retailers.</p>
              <p style={{ fontSize: ".82rem", color: "#6b7280", marginTop: 6 }}><strong>Retailer</strong> — Browse premium fabrics and place bulk orders directly.</p>
            </div>
          )}
        </div>
      );
      /*
      case 3: return (
        <div style={S.verifyBox}>
          <div style={S.verifyIcon}>
            <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: "1.4rem", color: "#0d9488" }} />
          </div>
          <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "#111827", margin: "0 0 6px" }}>Verify Your Email</h3>
          <p style={{ fontSize: ".83rem", color: "#6b7280", marginBottom: 20 }}>
            We've sent a 6-digit code to <strong style={{ color: "#0d9488" }}>{regData.emailId}</strong>
          </p>
          <div style={{ position: "relative", marginBottom: 12 }}>
            <FontAwesomeIcon icon={faShieldAlt} style={S.fieldIcon} />
            <input type="text" value={otp} onChange={e => setOtp(e.target.value)}
              placeholder="Enter 6-digit OTP" maxLength={6} required
              style={{ ...S.input, textAlign: "center", fontSize: "1.1rem", letterSpacing: ".2em" }}
            />
          </div>
          <button type="button" onClick={sendOtp} disabled={isRegLoading}
            style={{ background: "none", border: "none", color: "#0d9488", fontSize: ".82rem", fontWeight: 600, cursor: "pointer", marginLeft: "auto", display: "block" }}>
            {isRegLoading ? "Sending…" : "Resend OTP"}
          </button>
        </div>
      );
      */
      case 3: return (
        <div style={S.reviewBox}>
          <h3 style={{ fontSize: ".95rem", fontWeight: 600, color: "#111827", marginBottom: 16 }}>Review Your Details</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              { label: "Username",  value: regData.userName },
              { label: "Email",     value: regData.emailId },
              { label: "Phone",     value: regData.phoneNo },
              { label: "Role",      value: regData.role },
              ...(regData.role === "manufacturer"
                ? [{ label: "Company", value: regData.companyName }, { label: "License", value: regData.businessLicense }]
                : []),
              ...(regData.role === "retailer" ? [{ label: "GST", value: regData.gstNumber }] : []),
            ].map((r, i) => (
              <div key={i} style={S.reviewRow}>
                <span style={S.reviewLabel}>{r.label}</span>
                <span style={S.reviewValue}>{r.value || "—"}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
  };

  const regBtnLabel = isRegLoading ? "Processing…"
    : step === 3 ? "Complete Registration"
    // : step === 3 ? "Verify OTP"
    : "Continue";


  /* ── Variants for Framer Motion ── */
  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 30 : -30,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction < 0 ? 30 : -30,
      opacity: 0,
    }),
  };

  // Direction logic for animation
  const direction = activeTab === "login" ? -1 : 1;

  return (
    <>
      <style>{css}</style>
      <div style={S.page}>

        {/* ── Left Branding Panel ── */}
        <div style={S.panel} className="auth-panel">
          <div style={S.texture} />
          <div style={S.panelContent}>
            <img src={LOGO} alt="GrowTextile" style={S.logo} />
            <p style={S.panelTag}>
              {activeTab === "login" ? "Premium Textile Marketplace" : "Join Our Network"}
            </p>
            
            {/* Animate Branding Text conditionally to smooth it out */}
            <AnimatePresence mode="wait">
              {activeTab === "login" ? (
                <motion.div key="login-brand"
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <h2 style={S.panelTitle}>Fabric Most Trusted</h2>
                  <p style={S.panelSub}>
                    Connect with manufacturers, retailers and textile experts across India.
                  </p>
                  <div style={S.dividerLine} />
                  {["500+ Premium Fabrics", "Verified Manufacturers", "Bulk Order Support"].map(t => (
                    <div key={t} style={S.bullet}>
                      <span style={S.bulletDot} />
                      <span style={S.bulletText}>{t}</span>
                    </div>
                  ))}
                </motion.div>
              ) : (
                <motion.div key="reg-brand"
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <h2 style={S.panelTitle}>Create Your Account</h2>
                  <p style={S.panelSub}>
                    Become part of India's fastest-growing textile marketplace. 
                    Connect, trade, and grow together.
                  </p>
                  <div style={S.dividerLine} />
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {STEPS.map((s) => {
                      const done = step > s.key;
                      const active = step === s.key;
                      return (
                        <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                            background: done ? "#5eead4" : active ? "rgba(255,255,255,.25)" : "rgba(255,255,255,.1)",
                            border: active ? "2px solid #5eead4" : "2px solid transparent", transition: "all .3s",
                          }}>
                            <FontAwesomeIcon icon={s.icon} style={{ fontSize: ".78rem", color: done ? "#134e4a" : "rgba(255,255,255,.8)" }} />
                          </div>
                          <span style={{
                            fontSize: ".83rem", fontWeight: active ? 600 : 400,
                            color: active ? "#fff" : done ? "#5eead4" : "rgba(255,255,255,.5)", transition: "all .3s",
                          }}>{s.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div style={S.orb1} />
          <div style={S.orb2} />
        </div>

        {/* ── Right Form Panel ── */}
        <div style={S.formPanel}>
          <div style={S.formInner}>

            {/* Tab switcher */}
            <div style={S.tabs}>
              <button type="button" onClick={() => switchTab('login')}
                  style={{...S.tab, ...(activeTab === "login" ? S.tabActive : {})}}
              >
                Login
              </button>
              <button type="button" onClick={() => switchTab('register')}
                  style={{...S.tab, ...(activeTab === "register" ? S.tabActive : {})}}
              >
                Register
              </button>
            </div>

            <AnimatePresence mode="wait" custom={direction} initial={false}>
              
              {/* ───────────────── LOGIN VIEW ───────────────── */}
              {activeTab === "login" && (
                <motion.div
                  key="loginView"
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ type: "tween", duration: 0.3, ease: "easeInOut" }}
                >
                  <p style={S.formTag}>Welcome back</p>
                  <h1 style={S.formTitle}>Sign in to your account</h1>
                  <div style={S.titleLine} />

                  <form onSubmit={handleLoginSubmit} style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 4 }}>
                    <Field label="Email Address" icon={faEnvelope} id="loginEmail" type="email"
                      value={loginData.emailId} onChange={e => setLoginData({ ...loginData, emailId: e.target.value })}
                      placeholder="you@example.com" required />

                    <Field label="Password" icon={faLock} id="loginPass">
                      <input id="loginPass" type={showLoginPass ? "text" : "password"}
                        value={loginData.password} onChange={e => setLoginData({ ...loginData, password: e.target.value })}
                        placeholder="Enter your password" required style={S.input}
                        onFocus={e => (e.target.style.borderColor = "#0d9488")} onBlur={e => (e.target.style.borderColor = "#e5e7eb")}
                      />
                      <button type="button" onClick={() => setShowLoginPass(!showLoginPass)} style={S.eyeBtn}>
                        <FontAwesomeIcon icon={showLoginPass ? faEyeSlash : faEye} style={{ fontSize: ".8rem", color: "#9ca3af" }} />
                      </button>
                    </Field>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: ".82rem", color: "#6b7280", cursor: "pointer" }}>
                        <input type="checkbox" style={{ accentColor: "#0d9488" }} />
                        Remember me
                      </label>
                      <Link to="/forgot-password" style={{ fontSize: ".82rem", color: "#0d9488", textDecoration: "none", fontWeight: 500 }}>
                        Forgot password?
                      </Link>
                    </div>

                    <button type="submit" disabled={isLoginLoading} style={S.submitBtn} className="auth-submit-btn">
                      {isLoginLoading ? (
                        <span style={S.btnSpan}><span style={S.spinner} className="auth-spinner" />Signing in…</span>
                      ) : (
                        <span style={S.btnSpan}>Sign In <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: ".8rem" }} /></span>
                      )}
                    </button>
                  </form>

                  <p style={{ textAlign: "center", fontSize: ".83rem", color: "#6b7280", marginTop: 24 }}>
                    Don't have an account?{" "}
                    <button onClick={() => switchTab('register')} style={{ color: "#0d9488", fontWeight: 600, background: "none", border: "none", padding: 0, cursor: "pointer" }}>
                      Register here
                    </button>
                  </p>
                </motion.div>
              )}

              {/* ───────────────── REGISTER VIEW ───────────────── */}
              {activeTab === "register" && (
                <motion.div
                  key="regView"
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ type: "tween", duration: 0.3, ease: "easeInOut" }}
                >
                  <p style={S.formTag}>Step {step} of {STEPS.length}</p>
                  <h1 style={S.formTitle}>{STEPS[step - 1].label}</h1>
                  <div style={S.titleLine} />

                  <div style={S.mobileDots} className="auth-mobile-dots">
                    {STEPS.map(s => (
                      <div key={s.key} style={{
                        width: step >= s.key ? 24 : 8, height: 8, borderRadius: 4,
                        background: step > s.key ? "#0d9488" : step === s.key ? "#0d9488" : "#e5e7eb",
                        opacity: step >= s.key ? 1 : .4, transition: "all .3s",
                      }} />
                    ))}
                  </div>

                  <form onSubmit={handleRegSubmit} style={{ marginTop: 24 }}>
                    {renderRegStep()}

                    <div style={S.btnRow}>
                      {step > 1 && (
                        <button type="button" onClick={() => setStep(s => s - 1)} style={S.backBtn}>
                          <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: ".78rem" }} /> Back
                        </button>
                      )}
                      <button type="submit" disabled={isRegLoading} style={S.submitBtn} className="auth-submit-btn">
                        {isRegLoading ? (
                          <span style={S.btnSpan}><span style={S.spinner} className="auth-spinner" />{regBtnLabel}</span>
                        ) : (
                          <span style={S.btnSpan}>{regBtnLabel} <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: ".78rem" }} /></span>
                        )}
                      </button>
                    </div>
                  </form>

                  <p style={{ textAlign: "center", fontSize: ".83rem", color: "#6b7280", marginTop: 20 }}>
                    Already have an account?{" "}
                    <button onClick={() => switchTab('login')} style={{ color: "#0d9488", fontWeight: 600, background: "none", border: "none", padding: 0, cursor: "pointer" }}>
                      Sign in
                    </button>
                  </p>
                </motion.div>
              )}

            </AnimatePresence>

          </div>
        </div>
      </div>
    </>
  );
};

/* ── Styles ─────────────────────────────────────────────────── */
const S = {
  page:       { display: "flex", minHeight: "100vh", fontFamily: "'Inter',system-ui,sans-serif" },
  panel:      { flex: "0 0 40%", background: "linear-gradient(145deg,#134e4a 0%,#0f766e 55%,#0d9488 100%)", position: "relative", overflow: "hidden", display: "flex", alignItems: "center" },
  texture:    { position: "absolute", inset: 0, backgroundImage: "repeating-linear-gradient(0deg,rgba(255,255,255,.04) 0,rgba(255,255,255,.04) 1px,transparent 1px,transparent 24px),repeating-linear-gradient(90deg,rgba(255,255,255,.04) 0,rgba(255,255,255,.04) 1px,transparent 1px,transparent 24px)", pointerEvents: "none" },
  panelContent:{ position: "relative", zIndex: 1, padding: "48px 40px", width: "100%" },
  logo:       { height: 48, marginBottom: 24, filter: "brightness(0) invert(1)" },
  panelTag:   { fontSize: ".68rem", fontWeight: 700, letterSpacing: ".16em", textTransform: "uppercase", color: "#5eead4", marginBottom: 8 },
  panelTitle: { fontSize: "1.7rem", fontWeight: 300, color: "#fff", margin: "0 0 12px", lineHeight: 1.2 },
  panelSub:   { fontSize: ".85rem", color: "rgba(255,255,255,.65)", lineHeight: 1.7, marginBottom: 28 },
  dividerLine:{ width: 32, height: 2, background: "#5eead4", marginBottom: 24 },
  bullet:     { display: "flex", alignItems: "center", gap: 10, marginBottom: 12 },
  bulletDot:  { width: 6, height: 6, borderRadius: "50%", background: "#5eead4", flexShrink: 0 },
  bulletText: { fontSize: ".85rem", color: "rgba(255,255,255,.8)" },
  orb1:       { position: "absolute", width: 280, height: 280, borderRadius: "50%", background: "radial-gradient(circle,rgba(255,255,255,.07) 0%,transparent 70%)", bottom: -90, right: -60, pointerEvents: "none" },
  orb2:       { position: "absolute", width: 180, height: 180, borderRadius: "50%", background: "radial-gradient(circle,rgba(255,255,255,.05) 0%,transparent 70%)", top: -50, left: -30, pointerEvents: "none" },
  formPanel:  { flex: 1, background: "#f9fafb", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 24px", overflowY: "auto" },
  formInner:  { width: "100%", maxWidth: 460 },
  tabs:       { display: "flex", gap: 0, marginBottom: 28, background: "#fff", border: "1px solid #e5e7eb", padding: 4, borderRadius: 8 },
  tab:        { flex: 1, textAlign: "center", padding: "10px 0", fontSize: ".85rem", fontWeight: 500, color: "#6b7280", textDecoration: "none", borderRadius: 6, transition: "all .2s", background: "none", border: "none", cursor: "pointer" },
  tabActive:  { background: "#0d9488", color: "#fff", fontWeight: 600 },
  formTag:    { fontSize: ".68rem", fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: "#0d9488", marginBottom: 4 },
  formTitle:  { fontSize: "1.5rem", fontWeight: 300, color: "#111827", margin: "0 0 4px" },
  titleLine:  { width: 28, height: 2, background: "#0d9488", marginBottom: 4 },
  mobileDots: { display: "none", gap: 6, alignItems: "center", marginTop: 12 },
  label:      { display: "block", fontSize: ".7rem", fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "#6b7280", marginBottom: 6 },
  fieldIcon:  { position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: ".8rem", color: "#9ca3af", pointerEvents: "none" },
  input:      { width: "100%", padding: "11px 12px 11px 36px", border: "1px solid #e5e7eb", background: "#fff", fontSize: ".88rem", color: "#111827", outline: "none", borderRadius: 6, boxSizing: "border-box", transition: "border-color .2s" },
  eyeBtn:     { position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 4 },
  hint:       { fontSize: ".7rem", color: "#9ca3af", marginTop: 4 },
  roleHint:   { background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 8, padding: "14px 16px", marginTop: 4 },
  verifyBox:  { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "28px 24px", textAlign: "center" },
  verifyIcon: { width: 56, height: 56, borderRadius: "50%", background: "#f0fdfa", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" },
  reviewBox:  { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "20px 20px" },
  reviewRow:  { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: "#f9fafb", borderRadius: 6, border: "1px solid #f3f4f6" },
  reviewLabel:{ fontSize: ".72rem", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#9ca3af" },
  reviewValue:{ fontSize: ".85rem", fontWeight: 500, color: "#111827", textTransform: "capitalize" },
  btnRow:     { display: "flex", gap: 10, marginTop: 24 },
  backBtn:    { display: "flex", alignItems: "center", gap: 7, padding: "12px 18px", border: "1px solid #e5e7eb", background: "#fff", borderRadius: 6, fontSize: ".85rem", fontWeight: 500, color: "#374151", cursor: "pointer" },
  submitBtn:  { marginTop: "12px", flex: 1, padding: "12px 0", background: "#0d9488", color: "#fff", border: "none", borderRadius: 6, fontSize: ".88rem", fontWeight: 600, cursor: "pointer" },
  btnSpan:    { display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
  spinner:    { width: 14, height: 14, border: "2px solid rgba(255,255,255,.3)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block" },
};

const css = `
  @media(max-width:768px){ .auth-panel { display:none !important; } .auth-mobile-dots { display:flex !important; } }
  .auth-submit-btn:hover:not(:disabled) { background:#0f766e !important; }
  .auth-submit-btn:disabled { opacity:.65; cursor:not-allowed; }
  .auth-spinner { animation:spin .7s linear infinite; }
  @keyframes spin { to { transform:rotate(360deg); } }
`;

export default AuthPage;
